import { useEffect, useRef, useState } from 'react'

import { useDistribution } from 'contexts/Distribution'

/**
 * Resumes a returning SDK visitor on the scene after their last answer.
 *
 * Runs once, as soon as the scenes are available, jumping `currentPage` to the
 * distribution's computed `resumeSceneIndex` (see `computeResumeSceneIndex`) before
 * the first scene renders — so already-answered scenes are skipped rather than
 * mounted/animated through. Returns `resumeResolved`, which the caller should gate
 * the initial scene render on so nothing paints until the resume position is settled.
 *
 * No-op (resolves immediately, no jump) when there is nothing to resume to — the
 * `resumeSceneIndex` gating (SDK-only, prior progress) lives in the Distribution context.
 */
export const useSceneResume = (setCurrentPage: (page: number) => void): boolean => {
  const { scenes, resumeSceneIndex } = useDistribution()
  const didResume = useRef(false)
  const [resumeResolved, setResumeResolved] = useState(false)

  useEffect(() => {
    if (didResume.current || !scenes) {
      return
    }
    didResume.current = true
    if (resumeSceneIndex && resumeSceneIndex > 0) {
      setCurrentPage(resumeSceneIndex)
    }
    setResumeResolved(true)
  }, [scenes, resumeSceneIndex, setCurrentPage])

  return resumeResolved
}
